import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import api from '../utils/api';
import { formatDate, formatLocalDateInput } from '../utils/appDateTime';
import { Button } from './ui/button';
import { Input } from './ui/input';
import DateInput from './ui/date-input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import useBranchLabels from '../hooks/useBranchLabels';
import { cn } from '../lib/utils';

const EVENT_TYPE_STYLES = {
  academic: {
    label: 'Academic',
    pill: 'border-l-sky-500 bg-sky-100 text-sky-800 border-sky-200 dark:border-l-sky-500 dark:bg-sky-200 dark:text-sky-900 dark:border-sky-300',
    dot: 'bg-sky-500',
  },
  cultural: {
    label: 'Cultural',
    pill: 'border-l-violet-500 bg-violet-100 text-violet-800 border-violet-200 dark:border-l-violet-500 dark:bg-violet-200 dark:text-violet-900 dark:border-violet-300',
    dot: 'bg-violet-500',
  },
  sports: {
    label: 'Sports',
    pill: 'border-l-emerald-500 bg-emerald-100 text-emerald-800 border-emerald-200 dark:border-l-emerald-500 dark:bg-emerald-200 dark:text-emerald-900 dark:border-emerald-300',
    dot: 'bg-emerald-500',
  },
  meeting: {
    label: 'Meeting',
    pill: 'border-l-amber-500 bg-amber-100 text-amber-800 border-amber-200 dark:border-l-amber-500 dark:bg-amber-200 dark:text-amber-900 dark:border-amber-300',
    dot: 'bg-amber-500',
  },
  holiday: {
    label: 'Holiday',
    pill: 'border-l-rose-500 bg-rose-100 text-rose-800 border-rose-200 dark:border-l-rose-500 dark:bg-rose-200 dark:text-rose-900 dark:border-rose-300',
    dot: 'bg-rose-500',
  },
  other: {
    label: 'Other',
    pill: 'border-l-slate-400 bg-slate-100 text-slate-700 border-slate-200 dark:border-l-slate-400 dark:bg-slate-200 dark:text-slate-800 dark:border-slate-300',
    dot: 'bg-slate-400',
  },
};

const getEventTypeStyle = (typeValue) => EVENT_TYPE_STYLES[typeValue] || EVENT_TYPE_STYLES.other;

const eventTypes = Object.entries(EVENT_TYPE_STYLES).map(([value, { label }]) => ({ value, label }));

const CalendarEvents = () => {
  const { user, canManageFeature } = useAuth();
  const { formatLabel } = useBranchLabels();
  const canManageEvents = canManageFeature('events');
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const { toast } = useToast();

  const emptyForm = () => ({
    title: '', description: '', event_type: 'other',
    start_date: '', end_date: '', location: '', is_all_day: true,
  });

  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => { fetchEvents(); }, [currentDate, user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => fetchEvents();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [currentDate, user?.school_id]);

  const fetchEvents = async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      
      const response = await api.get('/events', {
        params: {
          start_date: formatLocalDateInput(startOfMonth),
          end_date: formatLocalDateInput(endOfMonth),
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId && Number(academicYearId) > 0 ? { academic_year_id: Number(academicYearId) } : {}),
        }
      });
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch events');
    }
  };

  const toDateOnly = (value) => {
    if (!value) return null;
    const s = String(value);
    return s.split('T')[0].split(' ')[0];
  };

  const getEventTypeValue = (event) => event?.event_type || event?.extendedProps?.event_type || 'other';

  const eventToForm = (event) => ({
    title: event?.title || '',
    description: event?.extendedProps?.description || event?.description || '',
    event_type: getEventTypeValue(event),
    start_date: toDateOnly(event?.start || event?.start_date) || '',
    end_date: toDateOnly(event?.end || event?.end_date) || '',
    location: event?.extendedProps?.location || event?.location || '',
    is_all_day: event?.allDay ?? true,
  });

  const formatDisplayDate = (value) => formatDate(value);

  const buildPayload = () => ({
    ...formData,
    start_date: formData.start_date + 'T00:00:00',
    end_date: (formData.end_date || formData.start_date) + 'T23:59:59',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const isEdit = dialogMode === 'edit' && selectedEvent?.id;
    let succeeded = false;
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      if (isEdit) {
        await api.put(`/events/${selectedEvent.id}`, buildPayload());
        toast({ title: 'Updated', description: 'Event updated. Notifications send in background.' });
      } else {
        await api.post('/events', {
          ...buildPayload(),
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: Number(academicYearId) || undefined } : {}),
        });
        toast({ title: 'Success', description: 'Event created. Notifications send in background.' });
      }
      succeeded = true;
      closeDialog();
      fetchEvents();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed', variant: 'destructive' });
    } finally {
      if (!succeeded) {
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent?.id || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/events/${selectedEvent.id}`);
      toast({ title: 'Deleted', description: 'Event removed. Notifications send in background.' });
      setDeleteConfirmOpen(false);
      closeDialog();
      fetchEvents();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to delete', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setSelectedEvent(null);
    setDialogMode('create');
  };

  const closeDialog = () => {
    flushSync(() => {
      setIsDialogOpen(false);
      setSubmitting(false);
    });
    submittingRef.current = false;
    resetForm();
  };

  const openNewEvent = (date = null) => {
    if (!canManageEvents) return;
    resetForm();
    setDialogMode('create');
    if (date) {
      const dateStr = formatLocalDateInput(date);
      setFormData((prev) => ({ ...prev, start_date: dateStr, end_date: dateStr }));
    }
    setIsDialogOpen(true);
  };

  const openEventView = (event, e) => {
    e?.stopPropagation();
    setSelectedEvent(event);
    setDialogMode('view');
    setIsDialogOpen(true);
  };

  const startEdit = () => {
    if (!selectedEvent) return;
    setFormData(eventToForm(selectedEvent));
    setDialogMode('edit');
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, currentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= totalDays; i++) {
      days.push({ day: i, currentMonth: true, date: new Date(year, month, i) });
    }
    
    // Next month days
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, currentMonth: false });
    }
    
    return days;
  };

  const getEventsForDay = (date) => {
    if (!date) return [];
    const dateStr = formatLocalDateInput(date);
    return events.filter(event => {
      const eventStart = toDateOnly(event.start || event.start_date);
      const eventEnd = toDateOnly(event.end || event.end_date) || eventStart;
      if (!eventStart) return false;
      return dateStr >= eventStart && dateStr <= eventEnd;
    });
  };

  const days = generateCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const goToToday = () => setCurrentDate(new Date());

  return (
    <div
      className="relative space-y-8 pb-8"
      data-testid="calendar-page"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-cyan-500/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-40 -left-16 h-64 w-64 rounded-full bg-violet-500/10 blur-3xl"
      />

      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500/25 to-violet-600/25 ring-1 ring-white/10 shadow-lg shadow-cyan-500/5">
            <CalendarIcon className="h-7 w-7 text-cyan-600 dark:text-cyan-300" strokeWidth={1.75} />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                Calendar
              </h1>
              <span className="inline-flex items-center gap-1 rounded-full border border-cyan-500/25 bg-cyan-500/10 px-2.5 py-0.5 text-xs font-medium text-cyan-700 dark:text-cyan-200">
                <Sparkles className="h-3 w-3" />
                Events
              </span>
            </div>
            <p className="mt-1 max-w-md text-sm leading-relaxed text-muted-foreground">
              {canManageEvents
                ? 'Click a day to add; click an event to view or edit.'
                : 'Month view with color-coded events. Click an event for details.'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={goToToday}
            className="border-border bg-muted/80 text-foreground hover:bg-muted hover:text-foreground"
          >
            Today
          </Button>
        {canManageEvents && (
          <Button
            type="button"
            className="bg-gradient-to-r from-cyan-600 to-blue-600 shadow-md shadow-cyan-900/40 hover:from-cyan-500 hover:to-blue-500"
            data-testid="add-event-btn"
            onClick={() => openNewEvent()}
          >
            <Plus className="w-4 h-4 mr-2" /> Add Event
          </Button>
        )}
        </div>
      </div>

      <Card className="relative overflow-hidden border-border/80 bg-gradient-to-b from-card/95 to-background/95 shadow-xl shadow-black/10 ring-1 ring-border/40 dark:from-slate-800/90 dark:to-slate-900/90">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
        <CardHeader className="space-y-0 border-b border-border/60 bg-background/40 px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => navigateMonth(-1)}
              className="h-10 w-10 shrink-0 rounded-xl text-muted-foreground hover:bg-muted/80 hover:text-foreground"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <h2 className="text-xl font-semibold text-foreground sm:text-2xl">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {events.length} event{events.length === 1 ? '' : 's'} this month
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => navigateMonth(1)}
              className="h-10 w-10 shrink-0 rounded-xl text-muted-foreground hover:bg-muted/80 hover:text-foreground"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-5">
          <div className="mb-3 grid grid-cols-7 gap-2">
            {weekDays.map((day, i) => (
              <div
                key={day}
                className={cn(
                  'rounded-lg py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider sm:text-xs',
                  i === 0 &&
                    'bg-rose-100 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-300/25 dark:text-rose-100 dark:ring-rose-300/40',
                  i === 6 && 'bg-slate-50 text-muted-foreground dark:bg-muted/30',
                  i !== 0 && i !== 6 && 'bg-muted/25 text-muted-foreground',
                )}
              >
                {day}
                {i === 0 && (
                  <span className="mt-0.5 block text-[9px] font-normal normal-case tracking-normal text-rose-600 dark:text-rose-300">
                    Holiday
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              const dayEvents = day.date ? getEventsForDay(day.date) : [];
              const isToday =
                day.date &&
                day.date.toDateString() === new Date().toDateString();
              const isSundayColumn = index % 7 === 0;
              const isSaturdayColumn = index % 7 === 6;

              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={day.currentMonth ? 0 : -1}
                  onClick={() => day.currentMonth && canManageEvents && openNewEvent(day.date)}
                  onKeyDown={(e) => {
                    if (!day.currentMonth || !canManageEvents) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openNewEvent(day.date);
                    }
                  }}
                  className={cn(
                    'group relative flex min-h-[5.5rem] flex-col rounded-xl border p-2 transition-all duration-200 sm:min-h-[6.75rem] sm:p-2.5',
                    isSundayColumn &&
                      'border-rose-200/60 bg-rose-50/80 dark:border-rose-300/35 dark:bg-rose-300/20',
                    isSundayColumn &&
                      day.currentMonth &&
                      'cursor-pointer hover:z-10 hover:border-rose-300 hover:bg-rose-100 hover:shadow-lg hover:shadow-rose-200/50 dark:hover:border-rose-300/50 dark:hover:bg-rose-300/30 dark:hover:shadow-rose-900/20',
                    isSundayColumn &&
                      !day.currentMonth &&
                      'cursor-default border-rose-200/40 bg-rose-50/40 opacity-60 dark:border-rose-300/20 dark:bg-rose-300/10 dark:opacity-40',
                    !isSundayColumn &&
                      !day.currentMonth &&
                      'cursor-default border-transparent bg-background/40 opacity-45 dark:opacity-40',
                    !isSundayColumn &&
                      day.currentMonth &&
                      'cursor-pointer border-border/40 bg-card/40 hover:z-10 hover:border-cyan-500/35 hover:bg-muted/55 hover:shadow-lg hover:shadow-cyan-950/20 dark:border-slate-600/50 dark:bg-slate-800/80 dark:hover:border-cyan-400/40 dark:hover:bg-slate-700/90',
                    !isSundayColumn &&
                      isSaturdayColumn &&
                      day.currentMonth &&
                      'bg-slate-50 dark:bg-slate-800/60',
                    isToday &&
                      !isSundayColumn &&
                      'border-cyan-400/70 bg-gradient-to-br from-cyan-500/15 to-blue-500/10 ring-2 ring-primary/50 ring-offset-2 ring-offset-background dark:from-cyan-400/25 dark:to-blue-400/20',
                    isToday &&
                      isSundayColumn &&
                      'border-cyan-400/70 bg-gradient-to-br from-cyan-500/15 via-rose-50/90 to-rose-100/80 ring-2 ring-cyan-400/50 ring-offset-2 ring-offset-background dark:from-cyan-400/25 dark:via-rose-300/25 dark:to-rose-300/20 dark:ring-cyan-400/50',
                  )}
                >
                  <div className="flex items-start justify-between gap-1">
                    <span
                      className={cn(
                        'inline-flex h-7 w-7 items-center justify-center rounded-lg text-sm font-semibold tabular-nums',
                        !day.currentMonth &&
                          !isSundayColumn &&
                          'text-muted-foreground',
                        !day.currentMonth &&
                          isSundayColumn &&
                          'text-rose-400 dark:text-rose-400/70',
                        day.currentMonth &&
                          !isToday &&
                          !isSundayColumn &&
                          'text-foreground group-hover:text-foreground',
                        day.currentMonth &&
                          !isToday &&
                          isSundayColumn &&
                          'text-rose-800 dark:text-rose-200',
                        isToday &&
                          'bg-cyan-500/25 text-cyan-700 dark:text-cyan-100 shadow-inner',
                      )}
                    >
                      {day.day}
                    </span>
                    {isSundayColumn && day.currentMonth && !isToday && (
                      <span
                        className="rounded bg-rose-200/60 px-1 py-0.5 text-[9px] font-medium uppercase leading-none text-rose-700 ring-1 ring-rose-300/50 dark:bg-rose-200 dark:text-rose-800 dark:ring-rose-300"
                        title="Weekly holiday"
                      >
                        Off
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 flex flex-1 flex-col gap-1 overflow-hidden">
                    {dayEvents.slice(0, 2).map((event) => {
                      const typeValue = getEventTypeValue(event);
                      const typeStyle = getEventTypeStyle(typeValue);
                      return (
                        <button
                          key={event.id}
                          type="button"
                          title={event.title}
                          onClick={(e) => openEventView(event, e)}
                          className={cn(
                            'truncate rounded-md border border-l-[3px] px-1.5 py-0.5 text-left text-[10px] font-medium leading-tight shadow-sm transition hover:brightness-105 sm:text-xs',
                            typeStyle.pill,
                          )}
                        >
                          {formatLabel(event.title, { branch_name: event?.extendedProps?.branch_name ?? event?.branch_name })}
                        </button>
                      );
                    })}
                    {dayEvents.length > 2 && (
                      <div className="text-[10px] font-medium text-muted-foreground sm:text-xs">
                        +{dayEvents.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setIsDialogOpen(true); }}>
        <DialogContent className="border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' && 'Create New Event'}
              {dialogMode === 'view' && (selectedEvent?.title || 'Event Details')}
              {dialogMode === 'edit' && 'Edit Event'}
            </DialogTitle>
          </DialogHeader>

          {dialogMode === 'view' && selectedEvent ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'inline-flex rounded-full border border-l-[3px] px-2.5 py-0.5 text-xs font-medium capitalize',
                    getEventTypeStyle(getEventTypeValue(selectedEvent)).pill,
                  )}
                >
                  {getEventTypeStyle(getEventTypeValue(selectedEvent)).label}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <p><span className="text-muted-foreground">Start:</span> {formatDisplayDate(selectedEvent.start || selectedEvent.start_date)}</p>
                <p><span className="text-muted-foreground">End:</span> {formatDisplayDate(selectedEvent.end || selectedEvent.end_date)}</p>
                {(selectedEvent.extendedProps?.location || selectedEvent.location) ? (
                  <p><span className="text-muted-foreground">Location:</span> {selectedEvent.extendedProps?.location || selectedEvent.location}</p>
                ) : null}
                {(selectedEvent.extendedProps?.description || selectedEvent.description) ? (
                  <p className="whitespace-pre-wrap rounded-md border border-border/60 bg-muted/40 p-3">
                    {selectedEvent.extendedProps?.description || selectedEvent.description}
                  </p>
                ) : null}
              </div>
              {canManageEvents ? (
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={startEdit}>
                    <Pencil className="w-4 h-4 mr-2" /> Edit
                  </Button>
                  <Button type="button" variant="destructive" onClick={() => setDeleteConfirmOpen(true)}>
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                </div>
              ) : null}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-muted border-border" required />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-muted border-border" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="bg-muted border-border" placeholder="Optional" />
              </div>
              <div>
                <Label>Event Type</Label>
                <Select value={formData.event_type} onValueChange={(v) => setFormData({ ...formData, event_type: v })}>
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    {eventTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <DateInput value={formData.start_date} onChange={(v) => setFormData({ ...formData, start_date: v })}
                    className="bg-muted border-border" required />
                </div>
                <div>
                  <Label>End Date</Label>
                  <DateInput value={formData.end_date} onChange={(v) => setFormData({ ...formData, end_date: v })}
                    className="bg-muted border-border" />
                </div>
              </div>
              <div className="flex gap-2">
                {dialogMode === 'edit' ? (
                  <Button type="button" variant="outline" onClick={() => { setDialogMode('view'); setFormData(eventToForm(selectedEvent)); }}>
                    Cancel
                  </Button>
                ) : null}
                <Button type="submit" disabled={submitting} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {submitting
                    ? (dialogMode === 'edit' ? 'Saving…' : 'Creating…')
                    : (dialogMode === 'edit' ? 'Save Changes' : 'Create Event')}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this event?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{selectedEvent?.title}&quot; will be removed from the calendar. Users will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="border-border/80 bg-muted/60 ring-1 ring-white/5">
        <CardContent className="p-4 sm:p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Legend
          </p>
          <div className="flex flex-wrap gap-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-100 px-3 py-1.5 text-sm text-rose-700 shadow-sm dark:border-rose-300/50 dark:bg-rose-200/80 dark:text-rose-800">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-rose-500" />
              Sunday (weekly holiday)
            </div>
            {eventTypes.map((type) => (
              <div
                key={type.value}
                className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/50 px-3 py-1.5 text-sm text-foreground shadow-sm"
              >
                <span
                  className={cn('h-2.5 w-2.5 shrink-0 rounded-full', getEventTypeStyle(type.value).dot)}
                />
                {type.label}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarEvents;
